import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Link } from "react-router-dom";
import Button from "components/Button";
import Input from "components/form/Input";
import Label from "components/form/Label";
import Switch from "components/form/Switch";
import Tooltip from "components/Tooltip";
import useDarkMode from "utilities/hooks/useDarkMode";
import useFullscreen from "utilities/hooks/useFullScreen";

const AuthLogin = () => {
  const [darkMode, toggleDarkMode] = useDarkMode();
  const [isFullscreen, toggleFullscreen] = useFullscreen();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(false);
  const navigate = useNavigate();

  // Helper function to decode JWT
  const decodeJWT = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;
    return Date.now() >= decoded.exp * 1000;
  };

  // Function to refresh access token
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      console.log("No refresh token available");
      return null;
    }

    // Check if refresh token is expired
    if (isTokenExpired(refreshToken)) {
      console.log("Refresh token expired");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      return null;
    }

    setIsTokenRefreshing(true);
    try {
      const response = await fetch("https://api.kennapartners.com/api/v1/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${refreshToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Refresh failed with status: ${response.status}`);
      }

      const data = await response.json();
      const newAccessToken = data?.data?.access_token || data?.access_token;
      const newRefreshToken = data?.data?.refresh_token || data?.refresh_token;
      
      if (newAccessToken) {
        localStorage.setItem("access_token", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refresh_token", newRefreshToken);
        }
        return newAccessToken;
      }
      return null;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Clear tokens if refresh fails
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      return null;
    } finally {
      setIsTokenRefreshing(false);
    }
  };

  // Function to check token and refresh if needed
  const checkTokenAndRefresh = async () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return null;

    // If token is not expired, return it
    if (!isTokenExpired(accessToken)) {
      return accessToken;
    }

    // Token is expired, try to refresh
    console.log("Access token expired, attempting refresh...");
    return await refreshAccessToken();
  };

  // Handle login
  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
  
    try {
      const response = await fetch("https://api.kennapartners.com/api/v1/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        if (response.status === 401) {
          alert("Invalid username or password.");
        } else if (response.status === 400) {
          alert("Bad request. Please check your input.");
        } else {
          alert(`Login failed with status: ${response.status}`);
        }
        return;
      }
  
      // Extract both tokens
      const accessToken = data?.data?.access_token || data?.access_token;
      const refreshToken = data?.data?.refresh_token || data?.refresh_token;
  
      if (accessToken && refreshToken) {
        // Save both tokens
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", refreshToken);
        
        console.log("Login successful, tokens saved");
        navigate("/dashboard");
      } else {
        console.error("Tokens missing in response:", data);
        alert("Login response missing required tokens.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        alert("Network error. Please check your internet connection.");
      } else if (error.message.includes('JSON')) {
        alert("Invalid response format from server.");
      } else {
        alert("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Set up fetch interceptor for automatic token refresh
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (url, options = {}) => {
      // Skip interception for auth endpoints
      if (url.includes('/auth/')) {
        return originalFetch(url, options);
      }
      
      // Check and refresh token if needed
      const validToken = await checkTokenAndRefresh();
      if (!validToken) {
        // Redirect to login if no valid token
        navigate("/auth-login");
        return Promise.reject(new Error("No valid access token"));
      }
      
      // Add authorization header
      const headers = new Headers(options.headers || {});
      headers.set("Authorization", `Bearer ${validToken}`);
      
      // Make the request
      const response = await originalFetch(url, {
        ...options,
        headers
      });

      // If token expired during request, try again
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          headers.set("Authorization", `Bearer ${newToken}`);
          return originalFetch(url, {
            ...options,
            headers
          });
        } else {
          navigate("/auth-login");
          return Promise.reject(new Error("Session expired"));
        }
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [navigate]);

  // Check for existing tokens on mount
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
      
      if (accessToken && refreshToken) {
        if (!isTokenExpired(accessToken)) {
          navigate("/dashboard");
        } else {
          const newToken = await refreshAccessToken();
          if (newToken) {
            navigate("/dashboard");
          }
        }
      }
    };

    checkAuth();
  }, [navigate]);

  // Custom spinner component
  const Spinner = () => (
    <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
  );

  return (
    <>
      {/* Top Bar */}
      <section className="top-bar">
        {/* Brand */}
        <span className="brand">Kenna</span>

        <nav className="flex items-center ltr:ml-auto rtl:mr-auto">
          {/* Dark Mode */}
          <Tooltip content="Toggle Dark Mode">
            <Switch
              outlined
              checked={darkMode}
              onChange={() => toggleDarkMode()}
            />
          </Tooltip>

          {/* Fullscreen */}
          <Tooltip content="Fullscreen">
            <button
              className={classNames(
                "hidden lg:inline-block ltr:ml-3 rtl:mr-3 px-2 text-2xl leading-none la",
                {
                  "la-compress-arrows-alt": isFullscreen,
                  "la-expand-arrows-alt": !isFullscreen,
                }
              )}
              onClick={toggleFullscreen}
            ></button>
          </Tooltip>
        </nav>
      </section>

      <div className="container flex items-center justify-center mt-20 py-10">
        <div className="w-full md:w-1/2 xl:w-1/3">
          <div className="mx-5 md:mx-10">
            <h2 className="uppercase">It's Great To See You!</h2>
            <h4 className="uppercase">Login Here</h4>
          </div>
          <form className="card mt-5 p-5 md:p-10" onSubmit={handleLogin}>
            <div className="mb-5">
              <Label className="block mb-2" htmlFor="username">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="mb-5">
              <Label className="block mb-2" htmlFor="password">
                Password
              </Label>
              <label className="form-control-addon-within">
                <Input
                  id="password"
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="flex items-center ltr:pr-4 rtl:pl-4">
                  <button
                    type="button"
                    className="text-gray-300 dark:text-gray-700 la la-eye text-xl leading-none"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  ></button>
                </span>
              </label>
            </div>
            <div className="flex justify-center">
              <Button 
                className="uppercase flex items-center gap-2" 
                type="submit"
                disabled={isLoading || isTokenRefreshing}
              >
                {(isLoading || isTokenRefreshing) && <Spinner />}
                {isLoading ? "Logging in..." : 
                 isTokenRefreshing ? "Refreshing session..." : "Login"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AuthLogin;